<script setup>
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useConfirm } from "@/shared/ui/alert-dialog";
import { Spinner } from "@/shared/ui/spinner";
import { Empty } from "@/shared/ui/empty";
import ParticipantSectionGroup from "./ParticipantSectionGroup.vue";
import AttorneyLinksBlock from "./AttorneyLinksBlock.vue";
import { filterStepsForParticipant } from "@/shared/lib/queueStepFilter";
import { ParticipantRole } from "@/shared/config/participantRoles";

const { t } = useI18n();
const confirm = useConfirm();

const props = defineProps({
  participants: {
    type: Array,
    default: () => [],
  },
  participantTypes: {
    type: Array,
    default: () => [],
  },
  stepsEntityTypes: {
    type: Object,
    default: () => ({}),
  },
  activeService: {
    type: String,
    default: null,
  },
  selectedParticipant: {
    type: Object,
    default: null,
  },
  isAccessLoading: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  requestedServices: {
    type: Map,
    default: () => new Map(),
  },
  requestedServicesData: {
    type: Map,
    default: () => new Map(),
  },
  attorneyLinks: {
    type: Array,
    default: () => [],
  },
  attorneyLinksLoading: {
    type: Boolean,
    default: false,
  },
  editable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "update-data",
  "select:step",
  "select:participant",
  "edit:participant",
  "open:attorney-links",
  "insert:saved-fields",
]);

const expandedAttorneyUuids = ref(new Set());

const getFio = (item) => {
  return [item.first_name, item.last_name, item.middle_name]
    .filter(Boolean)
    .join(" ");
};

const getIin = (item) => item.identifier || item.iin;

const getCompany = (item) => item?.params?.company_name || "";

const getBin = (item) => item?.params?.company_bin;

function toggleAttorneyLinks(participantUuid) {
  if (!participantUuid) return;
  const next = new Set(expandedAttorneyUuids.value);
  if (next.has(participantUuid)) {
    next.delete(participantUuid);
  } else {
    next.add(participantUuid);
    emit("open:attorney-links");
  }
  expandedAttorneyUuids.value = next;
}

function isAttorneyExpanded(participantUuid) {
  return expandedAttorneyUuids.value.has(participantUuid);
}

function getAttorneyLinksFor(participant) {
  const all = props.attorneyLinks || [];
  if (!participant?.identifier) return all;
  const filtered = all.filter(
    (link) =>
      Array.isArray(link?.participants) &&
      link.participants.some((p) => p?.identifier === participant.identifier)
  );
  return filtered.length ? filtered : all;
}

async function confirmRemove(action) {
  try {
    await confirm(
      t("Вы действительно хотите открепить участника от заявления?"),
      t("Открепление"),
      {
        confirmButtonText: t("Открепить"),
        cancelButtonText: t("Отмена"),
        type: "danger",
      }
    );
    const participant = props.participants.find((p) => p.uuid === action.uuid);
    if (participant) {
      emit("update-data", participant);
    }
  } catch {
    // отменено
  }
}

function getParticipantRoleLabel(role) {
  const code = role?.participant_type_code;
  if (!code) return t("Участник");
  if (role?.participant_type_code_info?.name) {
    return role.participant_type_code_info.name;
  }
  const type = props.participantTypes.find((r) => r.code === code);
  return type?.name || type?.name_kz || code;
}

function changeService(step, participant) {
  emit("select:step", step, participant);
}

function getGroups(participant) {
  const isRecoverer =
    participant.participant_type_code === ParticipantRole.RECOVERER;
  const isAgent =
    participant.participant_type_code === ParticipantRole.DOVERENNYI;

  let items = [{ name: t("Ручной ввод"), code: "manual_input" }];

  // Сервисные шаги ГБД — для RECOVERER и DOVERENNYI.
  if (isRecoverer || isAgent) {
    const rawSteps =
      props.stepsEntityTypes?.[participant.participant_type] || [];
    const stepList = filterStepsForParticipant(
      rawSteps,
      participant.participant_type_code
    );
    items.push(...stepList);

    if (isAgent) {
      items = [
        ...items,
        {
          name: "Доверенность",
          code: "internal_databases",
        },
      ];
    }
  }

  if (participant?.service_fields?.manual) {
    items.push({ name: t("Сведения о лице"), code: "manual" });
  }

  return [
    {
      title: getParticipantRoleLabel(participant),
      action: {
        icon: "huge-close",
        uuid: participant.uuid,
      },
      key: "external_services",
      expanded: true,
      items,
    },
  ];
}

function selectParticipant(item) {
  emit("select:participant", item);
}
</script>

<template>
  <div v-if="participants?.length" class="flex flex-col gap-4">
    <div v-if="loading" class="flex justify-center items-center py-8">
      <Spinner size="sm" />
    </div>

    <template v-else>
      <div
        v-for="item in participants"
        :key="item.id || item.uuid"
        @click="selectParticipant(item)"
      >
        <ParticipantSectionGroup
          v-for="group in getGroups(item)"
          :key="group.title"
          :group="group"
          :activeService="activeService"
          :participant="item"
          :selected-participant="selectedParticipant"
          :isAccessLoading="isAccessLoading"
          :requestedServices="requestedServices"
          :requestedServicesData="requestedServicesData"
          :editable="editable"
          @change:service="(step) => changeService(step, item)"
          @edit:participant="(p) => emit('edit:participant', p)"
          @insert:saved-fields="(p) => emit('insert:saved-fields', p)"
          @handleClick="confirmRemove"
        >
          <template #test>
            <div class="flex flex-col gap-2 pb-3">
              <div class="flex flex-col">
                <div class="flex items-center gap-1">
                  <div
                    class="text-foreground text-sm font-semibold line-clamp-2"
                  >
                    {{ getFio(item) }}
                  </div>
                </div>

                <div
                  v-if="getIin(item)"
                  class="text-muted-foreground text-sm line-clamp-1"
                >
                  {{ getIin(item) }}
                </div>

                <button
                  v-if="item.links === true"
                  type="button"
                  class="inline-flex items-center gap-1 self-start text-xs font-medium text-primary hover:underline cursor-pointer bg-transparent border-0 p-0"
                  @click.stop="toggleAttorneyLinks(item.uuid)"
                >
                  <svg-icon name="huge-check-mark" class="w-3 h-3" />
                  {{ $t("Действует по доверенности") }}

                  <svg-icon
                    :name="
                      isAttorneyExpanded(item.uuid) ? 'huge-up' : 'huge-down'
                    "
                    class="w-3 h-3"
                  />
                </button>
              </div>
            </div>
          </template>

          <template #company>
            <div
              v-if="getCompany(item) || getBin(item)"
              class="flex flex-col pb-3"
            >
              <div class="flex items-center gap-1">
                <div class="text-foreground text-sm font-semibold line-clamp-2">
                  {{ getCompany(item) }}
                </div>
              </div>

              <div
                v-if="getBin(item)"
                class="text-muted-foreground text-sm line-clamp-1"
              >
                {{ getBin(item) }}
              </div>
            </div>
          </template>
        </ParticipantSectionGroup>

        <div
          v-if="item.links === true && isAttorneyExpanded(item.uuid)"
          class="px-2 pb-3"
          @click.stop
        >
          <AttorneyLinksBlock
            :links="getAttorneyLinksFor(item)"
            :loading="attorneyLinksLoading"
          />
        </div>
      </div>
    </template>
  </div>

  <Empty v-else>
    {{ $t("Участники не добавлены") }}
  </Empty>
</template>

<style scoped lang="scss"></style>
